import React, { useState, useEffect } from "react";

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  danger?: boolean;
  darkMode?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  confirmButtonText = "Confirm",
  cancelButtonText = "Cancel",
  danger = true,
  darkMode = false,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setInputValue("");
      setError("");
      // Focus the input when modal opens
      setTimeout(() => {
        const input = document.getElementById("confirmation-input");
        input?.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (inputValue.trim() !== confirmText) {
      setError(`Please type "${confirmText}" to confirm`);
      return;
    }
    onConfirm();
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleConfirm();
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className={`relative w-full max-w-md rounded-lg shadow-2xl ${
          darkMode ? "bg-gray-800" : "bg-white"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b ${
            darkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <h3
            className={`text-xl font-bold ${
              darkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {title}
          </h3>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <p
            className={`mb-4 ${
              darkMode ? "text-gray-300" : "text-gray-700"
            }`}
          >
            {message}
          </p>

          <div className="mb-4">
            <label
              htmlFor="confirmation-input"
              className={`block text-sm font-medium mb-2 ${
                darkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Type <span className="font-bold">{confirmText}</span> to confirm:
            </label>
            <input
              id="confirmation-input"
              type="text"
              maxLength={500}
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setError("");
              }}
              onKeyDown={handleKeyPress}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors ${
                darkMode
                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                  : "bg-white border-gray-300 text-gray-900"
              } ${
                error
                  ? darkMode
                    ? "border-red-500"
                    : "border-red-500"
                  : ""
              }`}
              placeholder={confirmText}
              autoComplete="off"
            />
            {error && (
              <p className="mt-2 text-sm text-red-500">{error}</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          className={`px-6 py-4 border-t flex justify-end gap-3 ${
            darkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <button
            onClick={onClose}
            className={`px-4 py-2 border rounded-lg font-semibold transition-all duration-200 ${
              darkMode
                ? "border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-gray-500"
                : "border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
            }`}
          >
            {cancelButtonText}
          </button>
          <button
            onClick={handleConfirm}
            className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg ${
              danger
                ? "bg-red-600 text-white hover:bg-red-700"
                : "bg-gradient-to-r from-brand-dark-blue to-brand-cyan text-white hover:from-brand-cyan hover:to-brand-dark-blue"
            }`}
          >
            {confirmButtonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;

